import { type CSSProperties } from 'react';

import { getWorkspaceLayoutPortalAnchorBottomClearancePx } from '~/components/v2/utils/workspaceLayoutBottomChrome';

/** Minimum height of the frosted selection bar shell (matches legacy FAB diameter). */
export const WORKSPACE_AGENT_SPACE_BAR_MIN_HEIGHT_PX = 48;

/** Hover / label expand — shell width, padding, and label share one duration so they stay in lockstep. */
export const FAB_EXPAND_MS = 300;

const FAB_EXPAND_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/** Shell/button width and padding — same clock as the label. */
export const getFabShellTransitionStyle = (): CSSProperties => ({
  transitionDuration: `${FAB_EXPAND_MS}ms`,
  transitionProperty: 'width, padding-right, padding-left, background-color, box-shadow, transform',
  transitionTimingFunction: FAB_EXPAND_EASE,
});

/** Label max-width, opacity, and margin — matched to `getFabShellTransitionStyle`. */
export const getFabLabelTransitionStyle = (shouldReduceMotion = false): CSSProperties => {
  if (shouldReduceMotion) {
    return { transition: 'none' };
  }

  return {
    transition: `max-width ${FAB_EXPAND_MS}ms ${FAB_EXPAND_EASE}, opacity ${FAB_EXPAND_MS}ms ${FAB_EXPAND_EASE}, margin-left ${FAB_EXPAND_MS}ms ${FAB_EXPAND_EASE}`,
  };
};

/** Gradient overlay on the expanded pill — same duration as the shell. */
export const getFabGradientTransitionStyle = (shouldReduceMotion = false): CSSProperties => {
  if (shouldReduceMotion) {
    return { transition: 'none' };
  }

  return {
    transitionDuration: `${FAB_EXPAND_MS}ms`,
    transitionTimingFunction: FAB_EXPAND_EASE,
  };
};

/** Multiplies every duration and delay in the agent space. */
export const WORKSPACE_AGENT_SPACE_TIME_SCALE = 1;

const SLIDE_DURATION_S = 0.4;

const getWorkspaceAgentSpaceBarSlideOff = (reservesStatusBarStrip: boolean) =>
  `calc(100% + ${getWorkspaceLayoutPortalAnchorBottomClearancePx(reservesStatusBarStrip)}px)`;

/** Off-screen position — own height plus portal bottom clearance (legacy status-bar layout). */
export const WORKSPACE_AGENT_SPACE_BAR_SLIDE_OFF = getWorkspaceAgentSpaceBarSlideOff(true);

/**
 * Rest position. Same `calc(percentage + px)` shape as `WORKSPACE_AGENT_SPACE_BAR_SLIDE_OFF`
 * so Motion can mix `y` (`0` vs `calc()` is NaN; `0px` vs `calc()` snaps). `0%` and `0px` are
 * the interpolation zeros.
 */
export const WORKSPACE_AGENT_SPACE_BAR_SLIDE_REST = 'calc(0% + 0px)';

// biome-ignore lint/style/noMagicNumbers: bezier control points only mean anything as a set
const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

export type WorkspaceAgentSpacePhase = 'fab' | 'unified' | 'hidden';

type WorkspaceAgentSpaceSlideTransition = {
  delay: number;
  duration: number;
  ease?: [number, number, number, number];
};

export type WorkspaceAgentSpaceShellVariant = {
  transition: WorkspaceAgentSpaceSlideTransition;
  y: string;
};

export type WorkspaceAgentSpaceShellVariants = {
  hidden: WorkspaceAgentSpaceShellVariant;
  shown: WorkspaceAgentSpaceShellVariant;
};

const createShellVariants = (
  shouldReduceMotion: boolean,
  reservesStatusBarStrip: boolean,
): WorkspaceAgentSpaceShellVariants => {
  const slide = shouldReduceMotion
    ? { duration: 0, delay: 0 }
    : {
        duration: SLIDE_DURATION_S * WORKSPACE_AGENT_SPACE_TIME_SCALE,
        delay: 0,
        ease: EASE_OUT,
      };

  return {
    hidden: {
      // Always off-screen. Reduced motion uses duration 0, so this snaps away and still
      // completes — matching REST would skip the transition and leave the bar stuck visible.
      y: getWorkspaceAgentSpaceBarSlideOff(reservesStatusBarStrip),
      transition: slide,
    },
    shown: {
      y: WORKSPACE_AGENT_SPACE_BAR_SLIDE_REST,
      transition: slide,
    },
  };
};

export const getWorkspaceAgentSpaceShellVariants = (shouldReduceMotion: boolean, reservesStatusBarStrip: boolean) =>
  createShellVariants(shouldReduceMotion, reservesStatusBarStrip);
